import React, { useMemo } from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import { QueueListItem, QueueSortKey } from '../../models';
import CmButton from '../../components/primitives/CmButton';
import EmptyState from '../../components/EmptyState/EmptyState';
import ErrorState from '../../components/EmptyState/ErrorState';
import LoadingState from '../../components/EmptyState/LoadingState';
import QueueCard from './QueueCard';
import styles from './queue.module.css';

export interface QueueCardListProps {
  items: QueueListItem[];
  sort: QueueSortKey;
  currentReviewerAccountId: number | null;
  isLoading: boolean;
  error: { error: string; param: string } | null;
  onRetry?: () => void;
  // Load-more affordance — `hasMore` toggles visibility, `isAppending` shows
  // the in-flight state. Omit `onLoadMore` and the button never renders.
  hasMore?: boolean;
  isAppending?: boolean;
  onLoadMore?: () => void;
  testId?: string;
}

export function sortItems(items: QueueListItem[], sort: QueueSortKey): QueueListItem[] {
  const copy = [...items];
  switch (sort) {
    case 'scoreDesc':
      copy.sort((a, b) => b.aiScore - a.aiScore);
      break;
    case 'ageDesc':
      copy.sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime());
      break;
    case 'ageAsc':
      copy.sort((a, b) => new Date(a.triggeredAt).getTime() - new Date(b.triggeredAt).getTime());
      break;
  }
  return copy;
}

export function QueueCardList({
  items,
  sort,
  currentReviewerAccountId,
  isLoading,
  error,
  onRetry,
  hasMore = false,
  isAppending = false,
  onLoadMore,
  testId,
}: QueueCardListProps): React.ReactElement {
  const sorted = useMemo(() => sortItems(items, sort), [items, sort]);

  if (error) {
    // ErrorState's onRetry/retryLabel are paired by its type — pass both or
    // neither, never a defined handler with no label.
    const retryProps = onRetry
      ? { onRetry, retryLabel: I18n.t(`${I18N_BASE}.queue.error.retry`) }
      : {};
    return (
      <ErrorState
        title={I18n.t(`${I18N_BASE}.queue.error.title`)}
        message={I18n.t(`${I18N_BASE}.queue.error.message`)}
        testId={testId ? `${testId}-error` : undefined}
        {...retryProps}
      />
    );
  }
  if (isLoading && items.length === 0) {
    return <LoadingState message={I18n.t(`${I18N_BASE}.queue.loading`)} testId={testId ? `${testId}-loading` : undefined} />;
  }
  if (sorted.length === 0) {
    return (
      <EmptyState
        title={I18n.t(`${I18N_BASE}.queue.empty.title`)}
        message={I18n.t(`${I18N_BASE}.queue.empty.message`)}
        testId={testId ? `${testId}-empty` : undefined}
      />
    );
  }

  return (
    <div className={styles.cardList} data-testid={testId}>
      {sorted.map((item) => (
        <QueueCard
          key={item.contentModerationEventId}
          item={item}
          currentReviewerAccountId={currentReviewerAccountId}
          testId={`queue-card-${item.contentModerationEventId}`}
        />
      ))}
      {onLoadMore && hasMore ? (
        <div className={styles.loadMoreRow}>
          <CmButton
            variant="ghost"
            onClick={onLoadMore}
            disabled={isAppending}
            testId={testId ? `${testId}-load-more` : undefined}
          >
            {I18n.t(
              isAppending
                ? `${I18N_BASE}.queue.loadingMore`
                : `${I18N_BASE}.queue.loadMore`,
            )}
          </CmButton>
        </div>
      ) : null}
    </div>
  );
}

export default QueueCardList;
